import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, Trash2, Search, RefreshCcw } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

const FILTERS = [
  { key: "all",     label: "Tümü" },
  { key: "blocked", label: "Yetkisiz" },
  { key: "allowed", label: "Başarılı" },
];

export default function AccessAttempts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/auth/attempts`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(() => toast.error("Kayıtlar yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const clearAll = async () => {
    if (!window.confirm("Tüm giriş denemesi kayıtları silinsin mi?")) return;
    try {
      await axios.delete(`${API}/auth/attempts`, { withCredentials: true });
      setRows([]); toast.success("Kayıtlar temizlendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const query = q.trim().toLocaleLowerCase("tr-TR");
  const visible = useMemo(() => rows.filter(r => {
    if (filter === "blocked" && r.allowed) return false;
    if (filter === "allowed" && !r.allowed) return false;
    if (!query) return true;
    return (
      (r.email || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (r.ip || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (r.name || "").toLocaleLowerCase("tr-TR").includes(query)
    );
  }), [rows, filter, query]);

  const counts = useMemo(() => ({
    all: rows.length,
    blocked: rows.filter(r => !r.allowed).length,
    allowed: rows.filter(r => r.allowed).length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="access-attempts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Güvenlik</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Yönetici Erişim Denemeleri</h1>
          <p className="text-slate-600 mt-2 text-sm">Yönetici paneline yapılan tüm Google giriş denemelerinin (yetkisiz + başarılı) tam listesi.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="attempts-refresh" onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCcw size={14}/> Yenile
          </button>
          <button data-testid="attempts-clear" onClick={clearAll}
            className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
            <Trash2 size={14}/> Temizle
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            data-testid={`attempts-filter-${f.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {f.label} <span className="ml-1 opacity-70 font-mono-nums">({counts[f.key]})</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="attempts-search"
                 placeholder="E-posta / IP ara..."
                 className="pl-9 pr-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white w-64"/>
        </div>
      </div>

      <div className="crisp-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Yükleniyor...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <ShieldCheck size={26} className="mx-auto mb-2 text-slate-400"/>
            <div className="text-sm">Henüz kayıt yok.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Zaman</th>
                  <th className="text-left px-4 py-3">Durum</th>
                  <th className="text-left px-4 py-3">E-posta</th>
                  <th className="text-left px-4 py-3">Ad</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Tarayıcı</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => (
                  <tr key={r.id} data-testid={`attempt-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-nums text-slate-700 whitespace-nowrap">{fmt(r.at)}</td>
                    <td className="px-4 py-3">
                      {r.allowed ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          <ShieldCheck size={11}/> İzinli
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                          <ShieldAlert size={11}/> Reddedildi
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono-nums text-slate-800">{r.email || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-slate-700">{r.name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 font-mono-nums text-slate-700">{r.ip || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 max-w-[280px] truncate" title={r.user_agent}>{r.user_agent || <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
